import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ConfirmationState } from '../types';

interface ErrorAlert {
  message: string;
  details?: string;
}

interface AdminContextValue {
  confirmationDialog: ConfirmationState | null;
  showConfirmation: (message: string, onConfirm: () => void | Promise<void>) => void;
  hideConfirmation: () => void;
  errorAlert: ErrorAlert | null;
  showError: (message: string, details?: string) => void;
  hideError: () => void;
}

const AdminContext = createContext<AdminContextValue | null>(null);

interface Props {
  children: React.ReactNode;
}

export function AdminProvider({ children }: Props) {
  const [confirmationDialog, setConfirmationDialog] =
    useState<ConfirmationState | null>(null);
  const [errorAlert, setErrorAlert] = useState<ErrorAlert | null>(null);

  const hideConfirmation = useCallback(() => {
    setConfirmationDialog(null);
  }, []);

  const hideError = useCallback(() => {
    setErrorAlert(null);
  }, []);

  const showError = useCallback((message: string, details?: string) => {
    setErrorAlert({ message, details });
  }, []);

  const showConfirmation = useCallback(
    (message: string, onConfirm: () => void | Promise<void>) => {
      setConfirmationDialog({
        message,
        onConfirm: async () => {
          try {
            await onConfirm();
          } catch (error) {
            console.error('Confirmation action failed:', error);
          } finally {
            hideConfirmation();
          }
        },
        onCancel: hideConfirmation,
      });
    },
    [hideConfirmation]
  );

  const value = useMemo(
    () => ({
      confirmationDialog,
      showConfirmation,
      hideConfirmation,
      errorAlert,
      showError,
      hideError,
    }),
    [confirmationDialog, showConfirmation, hideConfirmation, errorAlert, showError, hideError]
  );

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
}

export function useAdminContext() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdminContext must be used within AdminProvider');
  }
  return context;
}
